import { useCallback } from "react";

import { useApolloClient, useMutation } from "@apollo/client";
import { useIntl } from "react-intl";

import { deleteRemoteAssetsMutation } from "../api/mutations/deleteRemoteAssets";
import {
    type DeleteRemoteAssetsMutation,
    type DeleteRemoteAssetsMutationVariables,
} from "../api/mutations/deleteRemoteAssets.gql-generated";

import { messages } from "./messages.generated";
import { formatApolloError } from "./useErrorMessage";
import { type SetMutationStatus } from "./useMutationStatus";

interface DeleteRemoteAssetsResult {
    deleteAssets: (ids: string[]) => Promise<boolean>;
}

export const useDeleteRemoteAssets = (setStatus: SetMutationStatus): DeleteRemoteAssetsResult => {
    const intl = useIntl();
    const client = useApolloClient();

    const [deleteMutation] = useMutation<DeleteRemoteAssetsMutation, DeleteRemoteAssetsMutationVariables>(
        deleteRemoteAssetsMutation,
    );

    const deleteAssets = useCallback(
        async (ids: string[]) => {
            setStatus({ type: "pending", action: "bulkDelete" });
            try {
                const result = await deleteMutation({
                    variables: { ids },
                    refetchQueries: ["getRemoteAssetsQuota"],
                });
                const successIds = result.data?.deleteRemoteAssets.successIds ?? [];
                const failedCount = result.data?.deleteRemoteAssets.failedIds.length ?? 0;

                // Evict after mutation resolves so the detail page can
                // navigate away before the cache watcher fires a refetch
                for (const id of successIds) {
                    client.cache.evict({ id: client.cache.identify({ __typename: "RemoteAsset", id }) });
                }
                client.cache.gc();

                if (failedCount > 0) {
                    setStatus({
                        type: "error",
                        action: "bulkDelete",
                        message: intl.formatMessage(messages.bulkDeletePartialError, {
                            successCount: successIds.length,
                            failedCount,
                        }),
                    });
                    return false;
                } else {
                    setStatus({ type: "success", action: "bulkDelete" });
                    return true;
                }
            } catch (error) {
                console.error("Failed to delete remote assets:", error);
                setStatus({
                    type: "error",
                    action: "bulkDelete",
                    message: formatApolloError(error, intl),
                });
                return false;
            }
        },
        [client, deleteMutation, setStatus, intl],
    );

    return { deleteAssets };
};
